import React, {useEffect, useState} from "react";
import {Button, Stack, TextField} from "@mui/material";
import {useTranslation} from "react-i18next";
import DefaultDialog, {DefaultDialogSize} from "../../../components/Dialog/ClosableDialog";
import {useDeviceFirmwareUpdateMutation, useSessionQuery} from "../../../app/api/api";
import {Firmware, FirmwareField, FirmwareState} from "../../../app/api/model/firmware";
import {PermissionLevel} from "../../../app/api/model/user";
import {FilterOperator} from "../../../components/ExTable";
import dayjs from "dayjs";
import {DateTimePicker} from "@mui/x-date-pickers";
import {FirmwareAutocomplete} from "../../../components/input/AsyncAutocomplete/variants";

interface DeviceFwUpdateDialogProps {
    open: boolean;
    onClose: () => void;
    deviceIds?: number[];
    hardwareType?: string;
    defaultFirmwareId?: number;
}

export default function DeviceFwUpdateDialog({
    open,
    onClose,
    deviceIds,
    hardwareType,
    defaultFirmwareId,
}: DeviceFwUpdateDialogProps) {
    const {t} = useTranslation();
    const [firmwareValue, setFirmwareValue] = useState<Firmware | number | undefined>(defaultFirmwareId);
    const [scheduleAt, setScheduleAt] = useState<Date | undefined>(dayjs().toDate());
    const [deviceFirmwareUpdate] = useDeviceFirmwareUpdateMutation();
    const {data: session} = useSessionQuery();

    useEffect(() => {
        setFirmwareValue(defaultFirmwareId);
    }, [defaultFirmwareId]);

    const handleClose = () => {
        setFirmwareValue(defaultFirmwareId);
        setScheduleAt(undefined);
        onClose?.();
    };

    const handleSubmit = () => {
        if (firmwareValue && deviceIds) {
            deviceIds.forEach((deviceId) => {
                deviceFirmwareUpdate({
                    deviceId,
                    firmwareId: typeof firmwareValue === "number" ? firmwareValue : firmwareValue?.id,
                    scheduledAt: dayjs(scheduleAt).utc().toISOString(),
                });
            });
        }
        handleClose();
    };

    return (
        <DefaultDialog
            size={DefaultDialogSize.MEDIUM}
            open={open}
            titleKey={"action_updateFirmware"}
            onClose={handleClose}
            actions={
                <Button
                    disabled={
                        !firmwareValue ||
                        (typeof firmwareValue !== "number" && firmwareValue.id === defaultFirmwareId) ||
                        defaultFirmwareId === firmwareValue ||
                        !deviceIds ||
                        !deviceIds.length
                    }
                    color="primary"
                    onClick={handleSubmit}
                >
                    {t("action_apply")}
                </Button>
            }
        >
            <Stack spacing={2} pt={1}>
                <FirmwareAutocomplete
                    label={t("firmware_version")}
                    additionalFilters={[
                        {f: FirmwareField.HARDWARE_TYPE, o: FilterOperator.EQ, v: hardwareType || ""},
                        {
                            f: FirmwareField.S3_KEY,
                            o: FilterOperator.NOT,
                            v: "",
                        },
                        {
                            f: FirmwareField.STATE,
                            o:
                                session?.permissionLevel !== PermissionLevel.ADMIN
                                    ? FilterOperator.IN
                                    : FilterOperator.NOT,
                            v:
                                session?.permissionLevel !== PermissionLevel.ADMIN
                                    ? [FirmwareState.STABLE, FirmwareState.BETA]
                                    : FirmwareState.DEPRECATED,
                        },
                    ]}
                    value={firmwareValue}
                    onChange={(value) => setFirmwareValue(value)}
                />
                <DateTimePicker
                    label={t("device_firmwareUpdateScheduledAt")}
                    value={scheduleAt || dayjs().toDate()}
                    onChange={(value) => {
                        setScheduleAt(value || undefined);
                    }}
                    renderInput={(params) => <TextField {...params} />}
                    ampm={false}
                />
            </Stack>
        </DefaultDialog>
    );
}
